import isEqual from 'lodash/isEqual';
import type { LngLatLike, Map as MapLibreMap } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { Tab } from '@revelio/data-access';

import { useFetchTooltipData } from '../../../../../../shared/hooks/useFetchTooltipData';
import { useAllSelectionLists } from '../../../../filters/hooks/useAllSelectionLists';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  type PrimaryFilterId,
} from '../../../../filters/types/filters';
import { flattenMultiFiltersForTooltip } from '../../../../filters/utils/flattenMultiFiltersForTooltip';
import {
  getCompositionV2Filters,
  homogenizeFilters,
} from '../../../../filters/utils/getCompositionV2Filters';
import { getPostingFilters } from '../../../../filters/utils/getPostingFilters';
import type { MapHoverHandler } from '../types';
import type { PopupContentProps } from '../utils/overview-map-tooltip-content';
import { useDebounce } from './useDebounce';
import { useHoveredFeature } from './useHoveredFeature';

type TooltipFeature = {
  id: number;
  type: number;
  label?: unknown;
  location?: unknown;
} | null;

interface UseOverviewTooltipOptions {
  enabled?: boolean;
  buildFeature?: (
    props: Record<string, unknown>,
    layerId: string
  ) => Record<string, unknown> | TooltipFeature | null;
  metric?: string;
}

const TOOLTIP_DEBOUNCE_MS = 150;
const DEFAULT_TOOLTIP_WIDTH = 210;
const DEFAULT_TOOLTIP_HEIGHT = 230;
const MARGIN = 40;
const EMPTY_COMPOSITION_ENTITIES: [] = [];

type TooltipAnchor =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export function useOverviewTooltip(
  primaryDimension: PrimaryFilterId = FilterId.GEOGRAPHY,
  options: UseOverviewTooltipOptions = {}
) {
  const { enabled = true, buildFeature, metric } = options;

  const mapRef = useRef<MapLibreMap | null>(null);
  const measuredSizeRef = useRef({
    width: DEFAULT_TOOLTIP_WIDTH,
    height: DEFAULT_TOOLTIP_HEIGHT,
  });
  const rafIdRef = useRef<number | null>(null);
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    content: PopupContentProps | null;
    x: number;
    y: number;
    anchor: TooltipAnchor;
  }>({
    isOpen: false,
    content: null,
    x: 0,
    y: 0,
    anchor: 'bottom',
  });

  const [isInteracting, setIsInteracting] = useState(false);
  const lastContentRef = useRef<PopupContentProps | null>(null);
  const lastPointerMoveAtRef = useRef(0);
  const lastInteractionEndAtRef = useRef(0);
  const currentLngLatRef = useRef<LngLatLike | null>(null);

  const computeTooltipPosition = useCallback(
    (
      map: MapLibreMap,
      lngLat: LngLatLike,
      size: { width: number; height: number }
    ) => {
      const point = map.project(lngLat);
      const container = map.getContainer();
      const rect = container.getBoundingClientRect();
      const mapWidth = rect.width;
      const mapHeight = rect.height;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const windowPointerX = point.x + rect.x;
      const windowPointerY = point.y + rect.y;

      const bottomWindowInterference =
        windowHeight - windowPointerY - size.height - MARGIN < 0;
      const topWindowInterference = windowPointerY - size.height - MARGIN < 0;
      const leftWindowInterference = windowPointerX - size.width - MARGIN < 0;
      const rightWindowInterference =
        windowWidth - windowPointerX - size.width - MARGIN < 0;

      const leftMapInterference = point.x - size.width - MARGIN < 0;
      const rightMapInterference = mapWidth - point.x - size.width - MARGIN < 0;
      const topMapInterference = point.y - size.height - MARGIN < 0;
      const bottomMapInterference =
        mapHeight - point.y - size.height - MARGIN < 0;

      const {
        leftRightPreference,
        topBottomPreference,
      }: {
        leftRightPreference: 'left' | 'right' | undefined;
        topBottomPreference: 'top' | 'bottom' | undefined;
      } = (() => {
        let leftRightPreference: 'left' | 'right' | undefined;
        let topBottomPreference: 'top' | 'bottom' | undefined;

        if (leftWindowInterference) {
          leftRightPreference = 'left';
        } else if (rightWindowInterference) {
          leftRightPreference = 'right';
        }
        if (topWindowInterference) {
          topBottomPreference = 'top';
        } else if (bottomWindowInterference) {
          topBottomPreference = 'bottom';
        }

        if (leftMapInterference && !leftRightPreference) {
          leftRightPreference = 'left';
        }
        if (rightMapInterference && !leftRightPreference) {
          leftRightPreference = 'right';
        }
        if (topMapInterference && !topBottomPreference) {
          topBottomPreference = 'top';
        }
        if (bottomMapInterference && !topBottomPreference) {
          topBottomPreference = 'bottom';
        }

        return { leftRightPreference, topBottomPreference };
      })();

      let anchor: TooltipAnchor = 'bottom';
      if (leftRightPreference === 'left' && topBottomPreference === 'top') {
        anchor = 'top-left';
      } else if (
        leftRightPreference === 'left' &&
        topBottomPreference === 'bottom'
      ) {
        anchor = 'bottom-left';
      } else if (
        leftRightPreference === 'right' &&
        topBottomPreference === 'top'
      ) {
        anchor = 'top-right';
      } else if (
        leftRightPreference === 'right' &&
        topBottomPreference === 'bottom'
      ) {
        anchor = 'bottom-right';
      } else if (leftRightPreference === 'left') {
        anchor = 'left';
      } else if (leftRightPreference === 'right') {
        anchor = 'right';
      } else if (topBottomPreference === 'top') {
        anchor = 'top';
      } else if (topBottomPreference === 'bottom') {
        anchor = 'bottom';
      }

      const x = rect.left + point.x;
      const y = rect.top + point.y;

      return { x, y, anchor };
    },
    []
  );

  const updateTooltipPosition = useCallback(() => {
    if (!mapRef.current || !currentLngLatRef.current) return;
    const position = computeTooltipPosition(
      mapRef.current,
      currentLngLatRef.current,
      measuredSizeRef.current
    );
    setTooltipState((prev) =>
      prev.x === position.x &&
      prev.y === position.y &&
      prev.anchor === position.anchor
        ? prev
        : {
            ...prev,
            ...position,
          }
    );
  }, [computeTooltipPosition]);

  const handleTooltipResize = useCallback(
    (size: { width: number; height: number }) => {
      const widthChanged =
        Math.abs(size.width - measuredSizeRef.current.width) > 2;
      const heightChanged =
        Math.abs(size.height - measuredSizeRef.current.height) > 2;
      if (!widthChanged && !heightChanged) return;
      measuredSizeRef.current = size;
      if (tooltipState.isOpen) {
        updateTooltipPosition();
      }
    },
    [tooltipState.isOpen, updateTooltipPosition]
  );

  const { hover, report } = useHoveredFeature({
    buildFeature: (props, layerId) =>
      buildFeature ? buildFeature(props, layerId) : null,
    throttleMs: 1,
  });

  const reportRef = useRef(report);
  useEffect(() => {
    reportRef.current = report;
  }, [report]);

  const handleMapHover = useCallback<MapHoverHandler>(
    (props, layerId, lngLat) => {
      reportRef.current(props, layerId, lngLat);
    },
    []
  );

  const [debouncedFeature, setDebouncedFeature] =
    useState<TooltipFeature>(null);
  const { run: setFeatureDebounced, cancel: cancelDebounce } = useDebounce(
    (f: TooltipFeature) => setDebouncedFeature(f),
    TOOLTIP_DEBOUNCE_MS
  );

  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  // TODO: remove this once the rest of the dashboard support multi-filters
  const tooltipFilters = useMemo(
    () => flattenMultiFiltersForTooltip(effectiveFilters),
    [effectiveFilters]
  );

  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const postingTooltipFilters = useMemo(
    () => flattenMultiFiltersForTooltip(postingEffectiveFilters),
    [postingEffectiveFilters]
  );
  const selectionLists = useAllSelectionLists();

  // For td_users metric, use homogenization to expand role/RICS selections
  // to the most granular level (required by tooltip APIs)
  const shouldHomogenize = metric === 'td_users';

  const selectionListsForHomogenization = useMemo(
    () => (shouldHomogenize ? selectionLists : undefined),
    [selectionLists, shouldHomogenize]
  );

  const tooltipFiltersForQuery = useMemo(
    () =>
      shouldHomogenize && selectionListsForHomogenization
        ? homogenizeFilters(tooltipFilters, selectionListsForHomogenization)
        : tooltipFilters,
    [tooltipFilters, shouldHomogenize, selectionListsForHomogenization]
  );

  const compositionData = useMemo(
    () =>
      getCompositionV2Filters({
        primaryDimension,
        effectiveFilters: tooltipFilters,
        homogenize: shouldHomogenize,
        selectionListsForHomogenization,
      }),
    [
      primaryDimension,
      tooltipFilters,
      shouldHomogenize,
      selectionListsForHomogenization,
    ]
  );

  const postingFiltersBase = useMemo(
    () =>
      getPostingFilters(primaryDimension, postingTooltipFilters, {
        isSnapshot: true,
        homogenize: shouldHomogenize,
        selectionListsForHomogenization,
      }),
    [
      primaryDimension,
      postingTooltipFilters,
      shouldHomogenize,
      selectionListsForHomogenization,
    ]
  );

  const compositionEntities = useMemo(() => {
    if (metric === 'td_users') {
      return EMPTY_COMPOSITION_ENTITIES; // Entities come from tooltipFeature for map tooltips
    }
    return Array.isArray(compositionData.entities)
      ? compositionData.entities
      : EMPTY_COMPOSITION_ENTITIES;
  }, [compositionData.entities, metric]);

  // Keep the snapshot dates `getPostingFilters` derived from the page's date
  // filter (the selected snapshot, or a range's latest point, expanded to the
  // 2-week window postings need) so the tooltip reflects the selected date
  // rather than always the latest available week.
  const postingFilters = useMemo(
    () => ({
      ...postingFiltersBase,
      provider: [0],
      metric_mode: 'expected_hires',
    }),
    [postingFiltersBase]
  );

  const { loggedInUser } = useGetLoggedInUser();
  const userTabs = loggedInUser?.tabs || [];
  const hasPayAccess = userTabs.includes(Tab.Compensation);
  const hasPostingsAccess = userTabs.includes(Tab.Posting);

  const tooltipData = useFetchTooltipData({
    primaryDimension,
    tooltipFeature: debouncedFeature,
    compositionEntities,
    compositionFilters: compositionData.filters,
    // The native v3 derivation reads these directly, so v3-only filters like
    // activity survive instead of being lost to the v2 shape. Homogenized for
    // td_users exactly as the v2 builder does it.
    effectiveFilters: tooltipFiltersForQuery,
    postingFilters,
    skipPosting: !hasPostingsAccess,
    skipCompensation: !hasPayAccess,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!mapRef.current) return;

    if (isInteracting) {
      setTooltipState((prev) => ({ ...prev, isOpen: false }));
      cancelDebounce();
      setDebouncedFeature(null);
      lastContentRef.current = null;
      currentLngLatRef.current = null;
      return;
    }

    if (!hover.feature || !hover.coords) {
      setTooltipState((prev) => ({ ...prev, isOpen: false }));
      cancelDebounce();
      setDebouncedFeature(null);
      lastContentRef.current = null;
      currentLngLatRef.current = null;
      return;
    }

    const label = hover.feature?.label;
    const location = hover.feature?.location;
    const header = (() => {
      if (typeof label === 'string') return label;
      if (typeof location === 'string') return location;
      if (typeof label === 'number') return String(label);
      if (typeof location === 'number') return String(location);
      return '';
    })();
    const allowReappearPostInteraction =
      !debouncedFeature &&
      (lastInteractionEndAtRef.current === 0 ||
        lastPointerMoveAtRef.current > lastInteractionEndAtRef.current);

    const lngLat: LngLatLike = [hover.coords[0], hover.coords[1]];
    currentLngLatRef.current = lngLat;

    if (hover.changed || allowReappearPostInteraction) {
      const showMapTooltip = metric === 'td_users';
      const skeletonContent: PopupContentProps = {
        header,
        headcount: '-',
        growthRate: '-',
        postings: '-',
        timeToFill: '-',
        salaryDist: ['', '', ''],
        marketTightness: '-',
        isLoading: true,
        showMarketTightness: showMapTooltip,
      };

      const position = computeTooltipPosition(
        mapRef.current,
        lngLat,
        measuredSizeRef.current
      );
      setTooltipState({
        isOpen: true,
        content: skeletonContent,
        ...position,
      });
      setFeatureDebounced(hover.feature);
    } else {
      updateTooltipPosition();
    }
  }, [
    hover,
    enabled,
    cancelDebounce,
    setFeatureDebounced,
    isInteracting,
    debouncedFeature,
    metric,
    computeTooltipPosition,
    updateTooltipPosition,
  ]);

  const content = useMemo(() => {
    if (!tooltipData) return null;
    const showMapTooltip = metric === 'td_users';
    return {
      ...tooltipData,
      showMarketTightness: showMapTooltip,
    };
  }, [tooltipData, metric]);

  useEffect(() => {
    if (!enabled) return;
    if (!content) return;
    const featureKey = (f: TooltipFeature): string | null =>
      f ? `${f.id}:${f.type}` : null;
    const currentKey = featureKey(hover.feature);
    const requestedKey = featureKey(debouncedFeature);
    if (currentKey && requestedKey && currentKey === requestedKey) {
      const contentChanged = !isEqual(lastContentRef.current, content);
      if (contentChanged) {
        lastContentRef.current = content;
        setTooltipState((prev) => ({ ...prev, content }));
      }
    }
  }, [content, hover.feature, debouncedFeature, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!mapRef.current) return;
    if (!currentLngLatRef.current) return;
    if (!tooltipState.isOpen) return;

    const map = mapRef.current;
    const scheduleUpdate = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateTooltipPosition();
      });
    };

    map.on('move', updateTooltipPosition);
    map.on('zoom', updateTooltipPosition);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    return () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      map.off('move', updateTooltipPosition);
      map.off('zoom', updateTooltipPosition);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [enabled, tooltipState.isOpen, updateTooltipPosition]);

  const initializeTooltipHandlers = useCallback(
    (map: MapLibreMap) => {
      if (!enabled) return;
      mapRef.current = map;

      const handleZoomStart = () => setIsInteracting(true);
      const handleZoomEnd = () => {
        setIsInteracting(false);
        lastInteractionEndAtRef.current = performance.now();
      };
      const handleMoveStart = () => setIsInteracting(true);
      const handleMoveEnd = () => {
        setIsInteracting(false);
        lastInteractionEndAtRef.current = performance.now();
      };
      const handlePointerMove = () => {
        lastPointerMoveAtRef.current = performance.now();
      };

      map.on('zoomstart', handleZoomStart);
      map.on('zoomend', handleZoomEnd);
      map.on('movestart', handleMoveStart);
      map.on('moveend', handleMoveEnd);
      map.on('mousemove', handlePointerMove);

      return () => {
        map.off('zoomstart', handleZoomStart);
        map.off('zoomend', handleZoomEnd);
        map.off('movestart', handleMoveStart);
        map.off('moveend', handleMoveEnd);
        map.off('mousemove', handlePointerMove);
      };
    },
    [enabled]
  );

  return {
    feature: debouncedFeature,
    initializeTooltipHandlers,
    handleMapHover,
    tooltipState,
    handleTooltipResize,
  };
}
